import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TicketCategoryPicker, CategoryAvailabilityCard } from "./TicketCategoryPicker";
import { GuidedLanguageSummary } from "./GuidedLanguageSummary";
import { FreeTicketsCard, FreeTicketsList } from "./FreeTicketsSummary";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { ArrowLeft, TrendingUp, TrendingDown, DollarSign, Users, Receipt, Plus, Pencil, Trash2, Save, X, Search, ChevronDown, ChevronUp, Download } from "lucide-react";

// ── Helpers ──
const mergeCategoryMaps = (acc, next) => {
  if (!next || typeof next !== "object") return acc;
  const out = { ...acc };
  for (const [k, v] of Object.entries(next)) {
    out[k] = (out[k] || 0) + Number(v || 0);
  }
  return out;
};

const CategoryBreakdown = ({ data = {}, color = "slate", emptyLabel = "—", testId }) => {
  const entries = Object.entries(data || {}).filter(([, v]) => Number(v || 0) > 0);
  if (entries.length === 0) {
    return <span className="text-xs text-slate-400" data-testid={testId}>{emptyLabel}</span>;
  }
  const colorMap = {
    red: "bg-red-50 text-red-700 border-red-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  const cls = colorMap[color] || colorMap.slate;
  return (
    <div className="flex flex-wrap gap-1 justify-end" data-testid={testId}>
      {entries.map(([cat, amount]) => (
        <Badge key={cat} variant="outline" className={`text-[10px] ${cls}`} title={`${cat}: €${Number(amount).toFixed(2)}`}>
          {cat}: {Number(amount).toFixed(2)}
        </Badge>
      ))}
    </div>
  );
};

// ── Overview: list of all events with financial summaries ──
export const EventFinancialOverview = ({ onSelectEvent }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("event_date");
  const [sortDir, setSortDir] = useState("desc");
  const [yearFilter, setYearFilter] = useState("all");

  const loadFinancials = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (yearFilter !== "all") params.year = yearFilter;
      const res = await axios.get("/dashboard/event-financials", { params });
      setEvents(res.data || []);
    } catch (err) {
      toast.error("Failed to load event financials");
    } finally {
      setLoading(false);
    }
  }, [yearFilter]);

  useEffect(() => { loadFinancials(); }, [loadFinancials]);

  // Year options inferred from the loaded events plus a fixed window around "now".
  const yearOptions = useMemo(() => {
    const now = new Date().getFullYear();
    const ys = new Set([now - 2, now - 1, now, now + 1]);
    events.forEach(e => {
      const y = e.event_date ? new Date(e.event_date).getFullYear() : null;
      if (y && !Number.isNaN(y)) ys.add(y);
    });
    return Array.from(ys).sort((a, b) => b - a);
  }, [events]);

  const filtered = events.filter(e =>
    e.event_title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortField] ?? "";
    const bv = b[sortField] ?? "";
    if (sortDir === "asc") return av > bv ? 1 : -1;
    return av < bv ? 1 : -1;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === "asc" ? <ChevronUp className="h-3 w-3 inline ml-1" /> : <ChevronDown className="h-3 w-3 inline ml-1" />;
  };

  const totals = events.reduce((acc, e) => ({
    income: acc.income + (e.paid_income || 0),
    expenses: acc.expenses + (e.total_expenses || 0),
    subsidies: acc.subsidies + (e.total_subsidies || 0),
    bookings: acc.bookings + (e.bookings_count || 0),
    expensesByCategory: mergeCategoryMaps(acc.expensesByCategory, e.expenses_by_category),
    subsidiesByCategory: mergeCategoryMaps(acc.subsidiesByCategory, e.subsidies_by_category),
  }), { income: 0, expenses: 0, subsidies: 0, bookings: 0, expensesByCategory: {}, subsidiesByCategory: {} });

  const netProfitLoss = totals.income + totals.subsidies - totals.expenses;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="financial-loading">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        <span className="ml-3 text-slate-600">Loading financial data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financial-overview">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Events</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="total-events-count">{events.length}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Receipt className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Income (Paid)</p>
                <p className="text-2xl font-bold text-emerald-700" data-testid="total-income">{totals.income.toFixed(2)}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Expenses</p>
                <p className="text-2xl font-bold text-red-700" data-testid="total-expenses">{totals.expenses.toFixed(2)}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <TrendingDown className="h-5 w-5 text-red-600" />
              </div>
            </div>
            <div className="mt-2">
              <CategoryBreakdown data={totals.expensesByCategory} color="red" emptyLabel="No expenses yet" testId="overview-expenses-breakdown" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-400">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Total Subsidies</p>
                <p className="text-2xl font-bold text-emerald-700" data-testid="total-subsidies">{totals.subsidies.toFixed(2)}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
            <div className="mt-2">
              <CategoryBreakdown data={totals.subsidiesByCategory} color="emerald" emptyLabel="No subsidies yet" testId="overview-subsidies-breakdown" />
            </div>
          </CardContent>
        </Card>
        <Card className={`border-l-4 ${netProfitLoss >= 0 ? 'border-l-emerald-500' : 'border-l-red-500'}`}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Net Profit/Loss</p>
                <p className={`text-2xl font-bold ${netProfitLoss >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="net-profit-loss">
                  {netProfitLoss >= 0 ? '+' : ''}{netProfitLoss.toFixed(2)}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-slate-600" />
              </div>
            </div>
            <p className="text-[10px] text-slate-500 mt-2">Paid income + Subsidies − Expenses</p>
          </CardContent>
        </Card>
      </div>

      {/* Search + Year filter + Export */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1 min-w-0">
          <div className="relative max-w-md w-full">
            <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
            <Input
              placeholder="Search events..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="financial-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600 whitespace-nowrap">Year:</span>
            <Select value={String(yearFilter)} onValueChange={(v) => setYearFilter(v === "all" ? "all" : parseInt(v, 10))}>
              <SelectTrigger className="w-32" data-testid="financial-year-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {yearOptions.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button
          onClick={async () => {
            try {
              const params = {};
              if (yearFilter !== "all") params.year = yearFilter;
              const res = await axios.get("/events/_/financial-export-all", { params, responseType: "blob" });
              const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
              const tag = yearFilter === "all" ? "" : `_${yearFilter}`;
              a.download = `Event_Financials_All${tag}_${today}.xlsx`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
              toast.success(yearFilter === "all" ? "All-time financials exported" : `Financials for ${yearFilter} exported`);
            } catch (err) {
              toast.error(err.response?.data?.detail || "Export failed");
            }
          }}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="export-all-financials-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          Export {yearFilter === "all" ? "all events" : yearFilter} Excel
        </Button>
      </div>

      {/* Events table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="cursor-pointer select-none font-semibold" onClick={() => toggleSort("event_title")}>
                    Event <SortIcon field="event_title" />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none font-semibold text-center" onClick={() => toggleSort("event_date")}>
                    Date <SortIcon field="event_date" />
                  </TableHead>
                  <TableHead className="text-center font-semibold">Bookings</TableHead>
                  <TableHead className="cursor-pointer select-none font-semibold text-right" onClick={() => toggleSort("paid_income")}>
                    Paid Income <SortIcon field="paid_income" />
                  </TableHead>
                  <TableHead className="text-right font-semibold">Pending</TableHead>
                  <TableHead className="cursor-pointer select-none font-semibold text-right" onClick={() => toggleSort("total_expenses")}>
                    Expenses <SortIcon field="total_expenses" />
                  </TableHead>
                  <TableHead className="font-semibold text-right">Expense breakdown</TableHead>
                  <TableHead className="cursor-pointer select-none font-semibold text-right" onClick={() => toggleSort("total_subsidies")}>
                    Subsidies <SortIcon field="total_subsidies" />
                  </TableHead>
                  <TableHead className="font-semibold text-right">Subsidy breakdown</TableHead>
                  <TableHead className="cursor-pointer select-none font-semibold text-right" onClick={() => toggleSort("profit_loss")}>
                    Profit/Loss <SortIcon field="profit_loss" />
                  </TableHead>
                  <TableHead className="font-semibold">Free / Admin tickets</TableHead>
                  <TableHead className="text-center font-semibold">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8 text-slate-500">No events found</TableCell>
                  </TableRow>
                ) : sorted.map(ev => (
                  <TableRow key={ev.event_id} className="hover:bg-slate-50 transition-colors" data-testid={`financial-event-row-${ev.event_id}`}>
                    <TableCell className="font-medium whitespace-normal break-words" data-testid={`financial-event-title-${ev.event_id}`}>{ev.event_title}</TableCell>
                    <TableCell className="text-center text-sm text-slate-600">{ev.event_date || '—'}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{ev.bookings_count}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium text-emerald-700">{(ev.paid_income || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-sm text-amber-600">{(ev.pending_income || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right font-medium text-red-600">{(ev.total_expenses || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      <CategoryBreakdown data={ev.expenses_by_category} color="red" emptyLabel="—" testId={`row-expenses-breakdown-${ev.event_id}`} />
                    </TableCell>
                    <TableCell className="text-right font-medium text-emerald-700">{(ev.total_subsidies || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right">
                      <CategoryBreakdown data={ev.subsidies_by_category} color="emerald" emptyLabel="—" testId={`row-subsidies-breakdown-${ev.event_id}`} />
                    </TableCell>
                    <TableCell className={`text-right font-bold ${(ev.profit_loss || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {(ev.profit_loss || 0) >= 0 ? '+' : ''}{(ev.profit_loss || 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="min-w-[150px]">
                      <FreeTicketsList data={ev.free_tickets} testId={`row-free-tickets-${ev.event_id}`} />
                    </TableCell>
                    <TableCell className="text-center">
                      <Button size="sm" variant="outline" onClick={() => onSelectEvent(ev.event_id)} data-testid={`view-detail-${ev.event_id}`}>
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ── Expense Dialog ──
const EXPENSE_CATEGORIES = [
  "Transport + Tips",
  "Event",
  "Guides",
  "Other (please specify)",
];

const ExpenseDialog = ({ open, onClose, onSave, expense, eventId }) => {
  const [form, setForm] = useState({ description: '', amount: '', category: '', category_other: '', date: '', notes: '' });

  useEffect(() => {
    if (expense) {
      const cat = expense.category || '';
      const isPredefined = EXPENSE_CATEGORIES.includes(cat);
      setForm({
        description: expense.description || '',
        amount: expense.amount?.toString() || '',
        category: isPredefined ? cat : 'Other (please specify)',
        category_other: isPredefined ? '' : cat,
        date: expense.date || '',
        notes: expense.notes || '',
      });
    } else {
      setForm({ description: '', amount: '', category: '', category_other: '', date: new Date().toISOString().slice(0, 10), notes: '' });
    }
  }, [expense, open]);

  const isOther = form.category === 'Other (please specify)';

  const handleSave = async () => {
    if (!form.description || !form.amount) {
      toast.error("Description and amount are required");
      return;
    }
    if (!form.category) {
      toast.error("Category is required");
      return;
    }
    if (isOther && !form.category_other.trim()) {
      toast.error("Please specify the category");
      return;
    }
    try {
      const finalCategory = isOther ? form.category_other.trim() : form.category;
      const payload = {
        description: form.description,
        amount: parseFloat(form.amount),
        category: finalCategory,
        date: form.date,
        notes: form.notes,
        event_id: eventId,
      };
      if (expense?.id) {
        await axios.put(`/events/${eventId}/expenses/${expense.id}`, payload);
        toast.success("Expense updated");
      } else {
        await axios.post(`/events/${eventId}/expenses`, payload);
        toast.success("Expense added");
      }
      onSave();
      onClose();
    } catch {
      toast.error("Failed to save expense");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle>{expense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Description *</label>
            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="expense-description" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Amount *</label>
              <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="expense-amount" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Category *</label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="expense-category">
                  <SelectValue placeholder="Select a category…" />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {isOther && (
            <div>
              <label className="text-sm font-medium text-slate-700">Please specify *</label>
              <Input
                value={form.category_other}
                onChange={e => setForm(f => ({ ...f, category_other: e.target.value }))}
                placeholder="Custom category"
                data-testid="expense-category-other"
              />
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-slate-700">Date</label>
            <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="expense-date" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Notes</label>
            <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="expense-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} data-testid="expense-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Subsidy Dialog ──
const SUBSIDY_CATEGORIES = ["EU Semester", "COPAS", "Other (please specify)"];
const INCOME_CATEGORIES = ["Cash payment", "Bank transfer payment", "Payment link", "Guest family member", "Other (please specify)"];

const IncomeDialog = ({ open, onClose, onSave, income, eventId, isCustomRows }) => {
  const [form, setForm] = useState({ description: '', amount: '', category: '', category_other: '', date: '', notes: '', linked_user_id: '', ticket_row_id: '', ticket_count: '' });

  // Member picker state — search-as-you-type against /admin/users/search.
  const [memberQuery, setMemberQuery] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [memberSearching, setMemberSearching] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    if (income) {
      setForm({
        description: income.description || '',
        amount: income.amount?.toString() || '',
        category: income.category || '',
        category_other: income.category_other || '',
        date: income.date || '',
        notes: income.notes || '',
        linked_user_id: income.linked_user_id || '',
        ticket_row_id: income.ticket_row_id || '',
        ticket_count: income.ticket_count ? String(income.ticket_count) : '',
      });
      // If the existing record has a linked member, hydrate the picker chip so
      // the admin sees who it's for (without a full user fetch we just show ID
      // as a fallback until the search results arrive).
      if (income.linked_user_id && income.linked_member_summary) {
        setSelectedMember(income.linked_member_summary);
      } else {
        setSelectedMember(null);
      }
    } else {
      setForm({ description: '', amount: '', category: '', category_other: '', date: new Date().toISOString().slice(0, 10), notes: '', linked_user_id: '', ticket_row_id: '', ticket_count: '' });
      setSelectedMember(null);
    }
    setMemberQuery('');
    setMemberResults([]);
  }, [income, open]);

  // Debounced member search (same endpoint used by AdminManualBookingDialog).
  useEffect(() => {
    const q = memberQuery.trim();
    if (selectedMember || q.length < 2) {
      setMemberResults([]);
      return undefined;
    }
    let cancelled = false;
    setMemberSearching(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.get('admin/users/search', { params: { q } });
        if (!cancelled) setMemberResults(res.data?.results || []);
      } catch {
        if (!cancelled) setMemberResults([]);
      } finally {
        if (!cancelled) setMemberSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [memberQuery, selectedMember]);

  const pickMember = (m) => {
    setSelectedMember(m);
    setMemberQuery('');
    setMemberResults([]);
    setForm(p => ({ ...p, linked_user_id: m.id }));
  };
  const clearMember = () => {
    setSelectedMember(null);
    setForm(p => ({ ...p, linked_user_id: '' }));
  };

  const isOther = form.category === 'Other (please specify)';
  const isPaymentLink = form.category === 'Payment link';
  const isGuestFamily = form.category === 'Guest family member';
  // Any category that surfaces the "Linked member" picker.
  const requiresLinkedMember = isPaymentLink || isGuestFamily;

  const handleSave = async () => {
    if (!form.description || !form.amount) {
      toast.error("Description and amount are required");
      return;
    }
    if (!form.category) {
      toast.error("Category is required");
      return;
    }
    if (isOther && !form.category_other.trim()) {
      toast.error("Please specify the income source");
      return;
    }
    if (requiresLinkedMember && !form.linked_user_id) {
      toast.error(
        isPaymentLink
          ? 'Please pick the member the "Payment link" is for'
          : 'Please pick the ACE member who invited this guest'
      );
      return;
    }
    try {
      const payload = {
        event_id: eventId,
        description: form.description,
        amount: parseFloat(form.amount),
        category: form.category,
        category_other: isOther ? form.category_other.trim() : '',
        date: form.date,
        notes: form.notes,
        linked_user_id: form.linked_user_id || null,
        ticket_row_id: form.ticket_row_id || '',
        ticket_count: form.ticket_row_id ? (parseInt(form.ticket_count, 10) || 1) : 0,
      };
      if (income?.id) {
        await axios.put(`/events/${eventId}/income/${income.id}`, payload);
        toast.success("Income updated");
      } else {
        await axios.post(`/events/${eventId}/income`, payload);
        toast.success("Income added");
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save income");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="income-dialog">
        <DialogHeader>
          <DialogTitle>{income ? 'Edit Income' : 'Add Income'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Description *</label>
            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="income-description" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Amount (€) *</label>
              <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="income-amount" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Category *</label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="income-category">
                  <SelectValue placeholder="Select a source…" />
                </SelectTrigger>
                <SelectContent>
                  {INCOME_CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {isOther && (
            <div>
              <label className="text-sm font-medium text-slate-700">Please specify *</label>
              <Input
                value={form.category_other}
                onChange={e => setForm(f => ({ ...f, category_other: e.target.value }))}
                placeholder="Income source name"
                data-testid="income-category-other"
              />
            </div>
          )}

          {/* Member picker — optional by default, required for "Payment link"
              (recipient of the emailed Stripe link) and "Guest family member"
              (the ACE member who invited the guest). */}
          <div>
            <label className="text-sm font-medium text-slate-700">
              Linked member {requiresLinkedMember && <span className="text-red-500">*</span>}
              {!requiresLinkedMember && <span className="text-slate-400 font-normal"> (optional)</span>}
              {isGuestFamily && <span className="text-slate-500 font-normal text-xs block">ACE member who invited this guest</span>}
            </label>
            {selectedMember ? (
              <div
                className="mt-1 flex items-start justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2"
                data-testid="income-selected-member"
              >
                <div className="min-w-0">
                  <p className="font-medium text-emerald-900 truncate">
                    {selectedMember.last_name} {selectedMember.first_name}
                    {selectedMember.member_number && (
                      <span className="ml-2 text-xs text-blue-700 font-semibold">#{selectedMember.member_number}</span>
                    )}
                  </p>
                  <p className="text-xs text-emerald-800 truncate">{selectedMember.email}</p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-emerald-800 hover:bg-emerald-100"
                  onClick={clearMember}
                  data-testid="income-clear-member-btn"
                >
                  Change
                </Button>
              </div>
            ) : (
              <>
                <Input
                  type="text"
                  value={memberQuery}
                  onChange={e => setMemberQuery(e.target.value)}
                  placeholder="Search by last name, email or member number…"
                  autoComplete="off"
                  data-testid="income-member-search"
                />
                {(memberQuery.trim().length >= 2 || memberSearching) && (
                  <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="income-member-results">
                    {memberSearching ? (
                      <p className="text-xs text-slate-500 italic p-3 text-center">Searching…</p>
                    ) : memberResults.length === 0 ? (
                      <p className="text-xs text-slate-500 italic p-3 text-center">No matching members</p>
                    ) : (
                      memberResults.map(m => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => pickMember(m)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                          data-testid={`income-member-${m.id}`}
                        >
                          <span className="font-medium text-slate-800">{m.last_name} {m.first_name}</span>
                          {m.member_number && <span className="ml-2 text-xs text-blue-600 font-semibold">#{m.member_number}</span>}
                          <span className="ml-2 text-xs text-slate-500">{m.email}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
            {isPaymentLink && (
              <p className="mt-1 text-xs text-slate-500">
                Save the income first, then use the <strong>Send payment link</strong> button on the row to email the Stripe checkout URL to this member.
              </p>
            )}
          </div>

          {isCustomRows && (
            <TicketCategoryPicker
              eventId={eventId}
              open={open}
              value={form.ticket_row_id}
              onChange={(v) => setForm(f => ({ ...f, ticket_row_id: v, ticket_count: v ? (f.ticket_count || '1') : '' }))}
              count={form.ticket_count}
              onCountChange={(v) => setForm(f => ({ ...f, ticket_count: v }))}
              testIdPrefix="income"
            />
          )}
          <div>
            <label className="text-sm font-medium text-slate-700">Date</label>
            <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="income-date" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Notes</label>
            <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="income-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} data-testid="income-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SubsidyDialog = ({ open, onClose, onSave, subsidy, eventId }) => {
  const [form, setForm] = useState({ description: '', amount: '', category: '', category_other: '', date: '', notes: '' });

  useEffect(() => {
    if (subsidy) {
      setForm({
        description: subsidy.description || '',
        amount: subsidy.amount?.toString() || '',
        category: subsidy.category || '',
        category_other: subsidy.category_other || '',
        date: subsidy.date || '',
        notes: subsidy.notes || '',
      });
    } else {
      setForm({ description: '', amount: '', category: '', category_other: '', date: new Date().toISOString().slice(0, 10), notes: '' });
    }
  }, [subsidy, open]);

  const isOther = form.category === 'Other (please specify)';

  const handleSave = async () => {
    if (!form.description || !form.amount) {
      toast.error("Description and amount are required");
      return;
    }
    if (!form.category) {
      toast.error("Category is required");
      return;
    }
    if (isOther && !form.category_other.trim()) {
      toast.error("Please specify the subsidy source");
      return;
    }
    try {
      const payload = {
        event_id: eventId,
        description: form.description,
        amount: parseFloat(form.amount),
        category: form.category,
        category_other: isOther ? form.category_other.trim() : '',
        date: form.date,
        notes: form.notes,
      };
      if (subsidy?.id) {
        await axios.put(`/events/${eventId}/subsidies/${subsidy.id}`, payload);
        toast.success("Subsidy updated");
      } else {
        await axios.post(`/events/${eventId}/subsidies`, payload);
        toast.success("Subsidy added");
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save subsidy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="subsidy-dialog">
        <DialogHeader>
          <DialogTitle>{subsidy ? 'Edit Subsidy' : 'Add Subsidy'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Description *</label>
            <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="subsidy-description" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Amount (€) *</label>
              <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="subsidy-amount" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Category *</label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="subsidy-category">
                  <SelectValue placeholder="Select a source…" />
                </SelectTrigger>
                <SelectContent>
                  {SUBSIDY_CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {isOther && (
            <div>
              <label className="text-sm font-medium text-slate-700">Please specify *</label>
              <Input
                value={form.category_other}
                onChange={e => setForm(f => ({ ...f, category_other: e.target.value }))}
                placeholder="Funding source name"
                data-testid="subsidy-category-other"
              />
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-slate-700">Date</label>
            <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="subsidy-date" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Notes</label>
            <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="subsidy-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} data-testid="subsidy-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Detail view: single event financial breakdown ──
export const EventFinancialDetail = ({ eventId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingComment, setEditingComment] = useState(null);
  const [commentValue, setCommentValue] = useState("");
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [subsidyDialogOpen, setSubsidyDialogOpen] = useState(false);
  const [editingSubsidy, setEditingSubsidy] = useState(null);
  const [incomeDialogOpen, setIncomeDialogOpen] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/events/${eventId}/financial-detail`);
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load event financial details");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { loadData(); }, [loadData]);

  const saveComment = async (bookingId) => {
    try {
      await axios.put(`/bookings/${bookingId}/admin-comment`, { admin_comments: commentValue });
      toast.success("Comment saved");
      setEditingComment(null);
      loadData();
    } catch {
      toast.error("Failed to save comment");
    }
  };

  const deleteExpense = async (expenseId) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await axios.delete(`/events/${eventId}/expenses/${expenseId}`);
      toast.success("Expense deleted");
      loadData();
    } catch {
      toast.error("Failed to delete expense");
    }
  };

  const deleteSubsidy = async (subsidyId) => {
    if (!window.confirm("Delete this subsidy?")) return;
    try {
      await axios.delete(`/events/${eventId}/subsidies/${subsidyId}`);
      toast.success("Subsidy deleted");
      loadData();
    } catch {
      toast.error("Failed to delete subsidy");
    }
  };

  const deleteIncome = async (incomeId) => {
    if (!window.confirm("Delete this income entry?")) return;
    try {
      await axios.delete(`/events/${eventId}/income/${incomeId}`);
      toast.success("Income entry deleted");
      loadData();
    } catch {
      toast.error("Failed to delete income");
    }
  };

  const sendIncomePaymentLink = async (incId) => {
    try {
      const res = await axios.post(`/events/${eventId}/income/${incId}/send-payment-link`);
      if (res.data?.email_sent) {
        toast.success("Payment link emailed to the member");
      } else {
        toast.warning("Payment link created, but email delivery failed. Please copy the link and share it manually.");
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send the payment link");
    }
  };

  const checkIncomePayment = async (incId) => {
    try {
      const res = await axios.post(`/events/${eventId}/income/${incId}/check-payment`);
      if (res.data?.paid && !res.data?.already_paid) {
        toast.success("Payment confirmed — income marked as paid");
      } else if (res.data?.paid && res.data?.already_paid) {
        toast.info("Already marked as paid");
      } else {
        toast.info("Not paid yet — no confirmed session found on Stripe");
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to check payment status");
    }
  };

  const handleCancelBooking = async (bookingId, firstName, lastName) => {
    if (!window.confirm(`Cancel the booking for ${firstName} ${lastName}? This will set the payment status to "cancelled".`)) return;
    try {
      await axios.put(`/bookings/${bookingId}/payment-status?payment_status=cancelled`);
      toast.success(`Booking for ${firstName} ${lastName} cancelled`);
      loadData();
    } catch {
      toast.error("Failed to cancel booking");
    }
  };

  const [showCancelled, setShowCancelled] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="detail-loading">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        <span className="ml-3 text-slate-600">Loading event details...</span>
      </div>
    );
  }

  if (!data) return <p className="text-center py-10 text-slate-500">No data available</p>;

  const { summary, attendees, expenses, subsidies = [], manual_income: incomeRows = [], is_matrix_pricing, is_custom_rows, custom_price_rows = [], section_1_label, section_2_label } = data;

  return (
    <div className="space-y-6" data-testid="financial-detail">
      {/* Back button + title + export */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="back-to-overview">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Overview
          </Button>
          <div>
            <h2 className="text-xl font-bold text-slate-900" data-testid="event-title">{data.event_title}</h2>
            <p className="text-sm text-slate-500">{data.event_date}</p>
          </div>
        </div>
        <Button
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700"
          onClick={async () => {
            try {
              const res = await axios.get(`/events/${eventId}/financial-export`, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const link = document.createElement('a');
              link.href = url;
              const disposition = res.headers['content-disposition'];
              let filename = 'Financial_Export.xlsx';
              if (disposition) {
                const match = disposition.match(/filename="?(.+?)"?$/);
                if (match) filename = match[1];
              }
              link.download = filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
              toast.success('Excel exported successfully');
            } catch {
              toast.error('Failed to export Excel');
            }
          }}
          data-testid="export-excel-btn"
        >
          <Download className="h-4 w-4 mr-2" /> Export Excel
        </Button>
      </div>

      {is_custom_rows && <CategoryAvailabilityCard categories={data.category_availability || []} />}
      <GuidedLanguageSummary
        attendees={attendees}
        isMatrix={!!is_matrix_pricing}
        onDownload={async (language) => {
          try {
            const res = await axios.get(`/events/${eventId}/guide-list`, { params: { language }, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            const match = (res.headers['content-disposition'] || '').match(/filename="?(.+?)"?$/);
            link.href = url;
            link.download = match ? match[1] : `Guide_list_${language}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Guide list downloaded");
          } catch {
            toast.error("Could not download the guide list");
          }
        }}
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
        <SummaryCard label="Total Bookings" value={summary.total_bookings} icon={<Users className="h-4 w-4" />} color="blue" testId="summary-bookings" />
        <SummaryCard label="Total Income" value={`${summary.total_income.toFixed(2)}`} icon={<DollarSign className="h-4 w-4" />} color="emerald" testId="summary-income" />
        <SummaryCard label="Paid Income" value={`${summary.paid_income.toFixed(2)}`} icon={<TrendingUp className="h-4 w-4" />} color="green" testId="summary-paid" />
        <SummaryCard label="Pending" value={`${summary.pending_income.toFixed(2)}`} icon={<DollarSign className="h-4 w-4" />} color="amber" testId="summary-pending" />
        <SummaryCard label="Expenses" value={`${summary.total_expenses.toFixed(2)}`} icon={<TrendingDown className="h-4 w-4" />} color="red" testId="summary-expenses" breakdown={summary.expenses_by_category} breakdownColor="red" />
        <SummaryCard label="Subsidies" value={`${(summary.total_subsidies || 0).toFixed(2)}`} icon={<TrendingUp className="h-4 w-4" />} color="emerald" testId="summary-subsidies" breakdown={summary.subsidies_by_category} breakdownColor="emerald" />
        <SummaryCard label="Other Income" value={`${(summary.total_manual_income || 0).toFixed(2)}`} icon={<DollarSign className="h-4 w-4" />} color="green" testId="summary-manual-income" breakdown={summary.manual_income_by_category} breakdownColor="green" />
        <SummaryCard
          label="Profit/Loss"
          value={`${summary.profit_loss >= 0 ? '+' : ''}${summary.profit_loss.toFixed(2)}`}
          color={summary.profit_loss >= 0 ? "emerald" : "red"}
          icon={summary.profit_loss >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
          testId="summary-profit-loss"
        />
        <FreeTicketsCard data={summary.free_tickets} />
      </div>

      {/* ── Attendees table ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Attendees ({attendees.filter(a => showCancelled || a.payment_status !== 'cancelled').length})
            {attendees.some(a => a.payment_status === 'cancelled') && (
              <span className="text-xs text-slate-400 ml-2">
                ({attendees.filter(a => a.payment_status === 'cancelled').length} cancelled)
              </span>
            )}
          </CardTitle>
          {(() => {
            const active = attendees.filter(a => showCancelled || a.payment_status !== 'cancelled');
            const sp = active.filter(a => a.source === 'season_pass');
            const single = active.length - sp.length;
            // Group season-pass attendees by the price-row label they bought.
            const rowCounts = {};
            for (const a of sp) {
              const key = a.season_pass_price_row_label
                || (a.seat_category ? (a.seat_category.charAt(0).toUpperCase() + a.seat_category.slice(1)) : 'Pass');
              rowCounts[key] = (rowCounts[key] || 0) + 1;
            }
            const rowChips = Object.entries(rowCounts);
            if (sp.length === 0 && single === 0) return null;
            return (
              <div className="flex flex-wrap items-center gap-2 mt-1" data-testid="attendee-source-summary">
                {sp.length > 0 && (
                  <Badge variant="outline" className="text-[11px] bg-purple-50 text-purple-800 border-purple-200" data-testid="summary-season-pass">
                    🎟️ Season Pass: {sp.length}
                    {rowChips.length > 0 && (
                      <span className="ml-1 text-purple-600">
                        ({rowChips.map(([k, v]) => `${k}:${v}`).join(" · ")})
                      </span>
                    )}
                  </Badge>
                )}
                {single > 0 && (
                  <Badge variant="outline" className="text-[11px] bg-blue-50 text-blue-800 border-blue-200" data-testid="summary-single-ticket">
                    🎫 Single: {single}
                  </Badge>
                )}
              </div>
            );
          })()}
          {attendees.some(a => a.payment_status === 'cancelled') && (
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="toggle-cancelled">
              <input
                type="checkbox"
                checked={showCancelled}
                onChange={(e) => setShowCancelled(e.target.checked)}
                className="rounded"
              />
              <span className="text-slate-600">Show cancelled</span>
            </label>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 text-xs">
                  <TableHead className="font-semibold whitespace-nowrap">Member ID</TableHead>
                  <TableHead className="font-semibold">First Name</TableHead>
                  <TableHead className="font-semibold">Last Name</TableHead>
                  <TableHead className="font-semibold">Category</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">Ticket type</TableHead>
                  <TableHead className="font-semibold">Mobile</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  {is_custom_rows ? (
                    <TableHead className="font-semibold whitespace-nowrap min-w-[220px]">Tickets bought</TableHead>
                  ) : is_matrix_pricing ? (
                    <>
                      <TableHead className="font-semibold text-center whitespace-nowrap">{section_1_label} (Price x Qty)</TableHead>
                      <TableHead className="font-semibold text-center whitespace-nowrap">{section_2_label} (Price x Qty)</TableHead>
                    </>
                  ) : (
                    <>
                      <TableHead className="font-semibold text-center whitespace-nowrap">Member (Price x Qty)</TableHead>
                      <TableHead className="font-semibold text-center whitespace-nowrap">Family (Price x Qty)</TableHead>
                    </>
                  )}
                  <TableHead className="font-semibold text-center">Minors</TableHead>
                  <TableHead className="font-semibold text-center">Visiting Family Members</TableHead>
                  <TableHead className="font-semibold text-right">Total</TableHead>
                  <TableHead className="font-semibold text-center">Status</TableHead>
                  <TableHead className="font-semibold whitespace-nowrap">Payment Date</TableHead>
                  <TableHead className="font-semibold">Guided Visit</TableHead>
                  <TableHead className="font-semibold min-w-[180px]">Comment</TableHead>
                  <TableHead className="font-semibold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {attendees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={18} className="text-center py-8 text-slate-500">
                      No bookings for this event
                    </TableCell>
                  </TableRow>
                ) : attendees.filter(att => showCancelled || att.payment_status !== 'cancelled').map(att => (
                  <TableRow key={att.booking_id} className="hover:bg-slate-50 text-sm" data-testid={`attendee-row-${att.booking_id}`}>
                    <TableCell className="font-mono text-xs text-slate-700" title={att.user_id} data-testid={`attendee-member-number-${att.booking_id}`}>
                      {att.member_number ? `#${att.member_number}` : '—'}
                    </TableCell>
                    <TableCell>{att.first_name}</TableCell>
                    <TableCell>{att.last_name}</TableCell>
                    <TableCell className="text-xs">
                      {att.member_category ? (
                        <Badge variant="outline" className={att.member_category.includes('Internal') ? 'border-blue-300 text-blue-700' : 'border-amber-300 text-amber-700'}>
                          {att.member_category.includes('Internal') ? 'Internal' : 'External'}
                        </Badge>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`attendee-source-${att.booking_id}`}>
                      {att.source === 'season_pass' ? (
                        <div className="flex flex-col gap-0.5">
                          <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-800 border-purple-200 w-fit">
                            🎟️ Season Pass
                          </Badge>
                          {(att.season_pass_price_row_label || att.seat_category) && (
                            <span className="text-[10px] text-purple-700 capitalize">
                              {att.season_pass_price_row_label || att.seat_category}
                            </span>
                          )}
                        </div>
                      ) : (
                        <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-800 border-blue-200">
                          🎫 Single
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{att.mobile || '—'}</TableCell>
                    <TableCell className="text-sm max-w-[160px] truncate" title={att.email}>{att.email}</TableCell>
                    {is_custom_rows ? (
                      <TableCell className="text-xs align-top" data-testid={`attendee-tickets-${att.booking_id}`}>
                        {(att.custom_row_selections || []).length === 0 && (!att.custom_row_minors || att.custom_row_minors.length === 0) ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <div className="space-y-1">
                            {(att.custom_row_selections || []).map((sel, i) => (
                              <div key={`sel-${i}`} className="flex items-center gap-1 flex-wrap">
                                <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">
                                  {sel.label} ×{sel.quantity}
                                </Badge>
                                <span className="text-[10px] text-slate-500">
                                  €{Number(sel.unit_price ?? 0).toFixed(2)} = €{Number(sel.subtotal ?? (sel.unit_price * sel.quantity)).toFixed(2)}
                                </span>
                              </div>
                            ))}
                            {(att.custom_row_minors || []).map((m, i) => (
                              <div key={`min-${i}`} className="flex items-center gap-1 flex-wrap">
                                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">
                                  👶 {m.first_name} {m.last_name} ({m.age}y)
                                </Badge>
                                <span className="text-[10px] text-slate-500">
                                  {m.label} · €{Number(m.unit_price ?? 0).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    ) : is_matrix_pricing ? (
                      <>
                        <TableCell className="text-center">
                          {att.section_1_tickets > 0 ? (
                            <span className="font-medium">{att.section_1_amount.toFixed(2)} x {att.section_1_tickets}</span>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          {att.section_2_tickets > 0 ? (
                            <span className="font-medium">{att.section_2_amount.toFixed(2)} x {att.section_2_tickets}</span>
                          ) : '—'}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className="text-center">
                          {att.member_tickets > 0 ? (
                            <span className="font-medium">{att.member_unit_price?.toFixed(2)} x {att.member_tickets}</span>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          {att.family_tickets > 0 ? (
                            <span className="font-medium">{att.family_unit_price?.toFixed(2)} x {att.family_tickets}</span>
                          ) : '—'}
                        </TableCell>
                      </>
                    )}
                    <TableCell className="text-center">
                      {att.minor_count > 0 ? <Badge variant="secondary">{att.minor_count}</Badge> : '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      {att.guest_count > 0 ? (
                        <div>
                          <Badge variant="outline" className="text-orange-700 border-orange-300">{att.guest_count}</Badge>
                          {att.guest_names?.length > 0 && (
                            <div className="text-xs text-slate-500 mt-1">{att.guest_names.join(', ')}</div>
                          )}
                        </div>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-semibold">{att.total_amount.toFixed(2)}</TableCell>
                    <TableCell className="text-center">
                      <PaymentStatusBadge status={att.payment_status} />
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{att.payment_date || '—'}</TableCell>
                    <TableCell className="text-sm capitalize">{att.guided_language || '—'}</TableCell>
                    <TableCell>
                      {editingComment === att.booking_id ? (
                        <div className="flex items-center gap-1">
                          <Input
                            className="h-7 text-xs"
                            value={commentValue}
                            onChange={e => setCommentValue(e.target.value)}
                            data-testid={`comment-input-${att.booking_id}`}
                          />
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => saveComment(att.booking_id)} data-testid={`comment-save-${att.booking_id}`}>
                            <Save className="h-3 w-3 text-emerald-600" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditingComment(null)}>
                            <X className="h-3 w-3 text-slate-400" />
                          </Button>
                        </div>
                      ) : (
                        <div
                          className="flex items-center gap-1 cursor-pointer group"
                          onClick={() => { setEditingComment(att.booking_id); setCommentValue(att.admin_comments || ''); }}
                          data-testid={`comment-cell-${att.booking_id}`}
                        >
                          <span className="text-xs text-slate-600 truncate max-w-[140px]">{att.admin_comments || '—'}</span>
                          <Pencil className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {att.payment_status !== 'cancelled' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 border-red-200 h-7 text-xs px-2"
                          onClick={() => handleCancelBooking(att.booking_id, att.first_name, att.last_name)}
                          data-testid={`cancel-booking-${att.booking_id}`}
                        >
                          <X className="h-3 w-3 mr-1" />
                          Cancel
                        </Button>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 text-xs">Cancelled</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* ── Expenses section ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Receipt className="h-5 w-5 text-red-600" />
              Expenses ({expenses.length})
            </CardTitle>
            <Button
              size="sm"
              onClick={() => { setEditingExpense(null); setExpenseDialogOpen(true); }}
              data-testid="add-expense-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add Expense
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 text-xs">
                  <TableHead className="font-semibold">Description</TableHead>
                  <TableHead className="font-semibold">Category</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold text-right">Amount</TableHead>
                  <TableHead className="font-semibold">Notes</TableHead>
                  <TableHead className="font-semibold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-6 text-slate-500">No expenses recorded</TableCell>
                  </TableRow>
                ) : expenses.map(exp => (
                  <TableRow key={exp.id} className="hover:bg-slate-50 text-sm" data-testid={`expense-row-${exp.id}`}>
                    <TableCell className="font-medium">{exp.description}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize text-xs">{exp.category || 'other'}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-600">{exp.date || '—'}</TableCell>
                    <TableCell className="text-right font-semibold text-red-700">{parseFloat(exp.amount || 0).toFixed(2)}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[160px] truncate">{exp.notes || '—'}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => { setEditingExpense(exp); setExpenseDialogOpen(true); }}
                          data-testid={`edit-expense-${exp.id}`}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-red-600 hover:text-red-800"
                          onClick={() => deleteExpense(exp.id)}
                          data-testid={`delete-expense-${exp.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {expenses.length > 0 && (
                  <TableRow className="bg-slate-50 font-semibold">
                    <TableCell colSpan={3} className="text-right">Total Expenses:</TableCell>
                    <TableCell className="text-right text-red-700">
                      {expenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* ── Subsidies section ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              Subsidies ({subsidies.length})
            </CardTitle>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => { setEditingSubsidy(null); setSubsidyDialogOpen(true); }}
              data-testid="add-subsidy-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add Subsidy
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 text-xs">
                  <TableHead className="font-semibold">Description</TableHead>
                  <TableHead className="font-semibold">Source</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold text-right">Amount</TableHead>
                  <TableHead className="font-semibold">Notes</TableHead>
                  <TableHead className="font-semibold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subsidies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-6 text-slate-500">No subsidies recorded</TableCell>
                  </TableRow>
                ) : subsidies.map(sub => {
                  const sourceLabel = sub.category === 'Other (please specify)'
                    ? (sub.category_other || 'Other')
                    : (sub.category || '—');
                  return (
                    <TableRow key={sub.id} className="hover:bg-slate-50 text-sm" data-testid={`subsidy-row-${sub.id}`}>
                      <TableCell className="font-medium">{sub.description}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-800 border-emerald-200">{sourceLabel}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-600">{sub.date || '—'}</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-700">{parseFloat(sub.amount || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-[160px] truncate">{sub.notes || '—'}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => { setEditingSubsidy(sub); setSubsidyDialogOpen(true); }}
                            data-testid={`edit-subsidy-${sub.id}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-600 hover:text-red-800"
                            onClick={() => deleteSubsidy(sub.id)}
                            data-testid={`delete-subsidy-${sub.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {subsidies.length > 0 && (
                  <TableRow className="bg-slate-50 font-semibold">
                    <TableCell colSpan={3} className="text-right">Total Subsidies:</TableCell>
                    <TableCell className="text-right text-emerald-700">
                      {subsidies.reduce((s, e) => s + parseFloat(e.amount || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* ── Income section (manual cash / bank-transfer / other) ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Income ({incomeRows.length})
            </CardTitle>
            <Button
              size="sm"
              className="bg-green-600 hover:bg-green-700"
              onClick={() => { setEditingIncome(null); setIncomeDialogOpen(true); }}
              data-testid="add-income-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add Income
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 text-xs">
                  <TableHead className="font-semibold">Description</TableHead>
                  <TableHead className="font-semibold">Source</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold text-right">Amount</TableHead>
                  <TableHead className="font-semibold">Notes</TableHead>
                  <TableHead className="font-semibold text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {incomeRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-6 text-slate-500">No income recorded</TableCell>
                  </TableRow>
                ) : incomeRows.map(inc => {
                  const sourceLabel = inc.category === 'Other (please specify)'
                    ? (inc.category_other || 'Other')
                    : (inc.category || '—');
                  const isPaymentLink = inc.category === 'Payment link';
                  const isPending = (inc.payment_status || 'paid') === 'pending';
                  const linkSent = !!inc.payment_link_sent_at;
                  return (
                    <TableRow key={inc.id} className="hover:bg-slate-50 text-sm" data-testid={`income-row-${inc.id}`}>
                      <TableCell className="font-medium">
                        {inc.description}
                        {inc.ticket_row_id && (
                          <Badge variant="outline" className="ml-2 text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200" data-testid={`income-ticket-category-${inc.id}`}>
                            {(custom_price_rows.find(r => r.id === inc.ticket_row_id)?.label) || 'Category'} × {inc.ticket_count || 1}
                          </Badge>
                        )}
                        {isPaymentLink && (
                          <div className="mt-1">
                            {isPending ? (
                              <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200" data-testid={`income-status-pending-${inc.id}`}>
                                {linkSent ? 'Link sent — awaiting payment' : 'Link not sent yet'}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200" data-testid={`income-status-paid-${inc.id}`}>
                                Paid
                              </Badge>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs bg-green-50 text-green-800 border-green-200">{sourceLabel}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-600">{inc.date || '—'}</TableCell>
                      <TableCell className={`text-right font-semibold ${isPending ? 'text-amber-700' : 'text-green-700'}`}>
                        {parseFloat(inc.amount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-[160px] truncate">{inc.notes || '—'}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1 flex-wrap">
                          {isPaymentLink && inc.linked_user_id && isPending && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                              onClick={() => sendIncomePaymentLink(inc.id)}
                              data-testid={`send-payment-link-${inc.id}`}
                              title={linkSent ? 'Resend the Stripe payment link email' : 'Create a Stripe payment link and email it to the member'}
                            >
                              {linkSent ? 'Resend link' : 'Send payment link'}
                            </Button>
                          )}
                          {isPaymentLink && linkSent && isPending && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              onClick={() => checkIncomePayment(inc.id)}
                              data-testid={`check-payment-${inc.id}`}
                              title="Ask Stripe whether the member has paid"
                            >
                              Check payment
                            </Button>
                          )}
                          {isPaymentLink && inc.stripe_payment_link_url && (
                            <a
                              href={inc.stripe_payment_link_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] text-blue-600 hover:text-blue-800 underline"
                              title="Open the Stripe payment link in a new tab"
                              data-testid={`open-payment-link-${inc.id}`}
                            >
                              Open
                            </a>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => { setEditingIncome(inc); setIncomeDialogOpen(true); }}
                            data-testid={`edit-income-${inc.id}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-600 hover:text-red-800"
                            onClick={() => deleteIncome(inc.id)}
                            data-testid={`delete-income-${inc.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {incomeRows.length > 0 && (
                  <TableRow className="bg-slate-50 font-semibold">
                    <TableCell colSpan={3} className="text-right">Total Income:</TableCell>
                    <TableCell className="text-right text-green-700">
                      {incomeRows.reduce((s, e) => s + parseFloat(e.amount || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <ExpenseDialog
        open={expenseDialogOpen}
        onClose={() => setExpenseDialogOpen(false)}
        onSave={loadData}
        expense={editingExpense}
        eventId={eventId}
      />

      <SubsidyDialog
        open={subsidyDialogOpen}
        onClose={() => setSubsidyDialogOpen(false)}
        onSave={loadData}
        subsidy={editingSubsidy}
        eventId={eventId}
      />

      <IncomeDialog
        open={incomeDialogOpen}
        onClose={() => setIncomeDialogOpen(false)}
        onSave={loadData}
        income={editingIncome}
        eventId={eventId}
        isCustomRows={!!data?.is_custom_rows}
      />
    </div>
  );
};

// ── Small helper components ──
const SummaryCard = ({ label, value, icon, color, testId, breakdown, breakdownColor }) => {
  const colorMap = {
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-600',
  };
  return (
    <Card>
      <CardContent className="pt-3 pb-2 px-3">
        <div className="flex items-center gap-2 mb-1">
          <div className={`h-6 w-6 rounded flex items-center justify-center ${colorMap[color] || colorMap.blue}`}>{icon}</div>
          <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide leading-tight">{label}</p>
        </div>
        <p className="text-lg font-bold text-slate-900" data-testid={testId}>{value}</p>
        {breakdown && Object.keys(breakdown).length > 0 && (
          <div className="mt-1.5">
            <CategoryBreakdown data={breakdown} color={breakdownColor || color} emptyLabel="" testId={`${testId}-breakdown`} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PaymentStatusBadge = ({ status }) => {
  const map = {
    paid: 'bg-emerald-100 text-emerald-800',
    confirmed: 'bg-blue-100 text-blue-800',
    pending: 'bg-amber-100 text-amber-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-slate-100 text-slate-700'}`} data-testid={`status-badge-${status}`}>
      {status || 'unknown'}
    </span>
  );
};

// ── Season Pass detail view (mirrors EventFinancialDetail's basic shape,
//   but shows a purchases table instead of attendees / expenses / subsidies) ──
export const SeasonPassFinancialDetail = ({ seasonPassId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/season-passes/${seasonPassId}/financial-detail`);
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load season pass financial details");
    } finally {
      setLoading(false);
    }
  }, [seasonPassId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="season-pass-detail-loading">
        Loading season pass details...
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-8 space-y-4" data-testid="season-pass-detail-empty">
        <Button variant="outline" onClick={onBack} data-testid="back-to-financials">← Back</Button>
        <p className="text-slate-500">Season pass details could not be loaded.</p>
      </div>
    );
  }

  const fin = data.financials || {};
  const purchases = data.purchases || [];
  const sp = data.season_pass || {};

  const statusColor = (s) => (
    s === 'paid' ? 'bg-emerald-100 text-emerald-800' :
    s === 'pending' ? 'bg-amber-100 text-amber-800' :
    s === 'refunded' ? 'bg-slate-200 text-slate-700' :
    'bg-slate-100 text-slate-600'
  );

  return (
    <div className="space-y-6" data-testid="season-pass-detail">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Button variant="outline" onClick={onBack} data-testid="back-to-financials" className="mb-3">
            ← Back to Financials
          </Button>
          <h2 className="text-xl font-bold text-slate-900" data-testid="season-pass-detail-title">
            {data.event_title}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Sales window: {sp.sales_window_start || '—'} → {sp.sales_window_end || '—'} · Status: <span className="font-medium">{sp.status || '—'}</span>
          </p>
        </div>
      </div>

      {/* Financial summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-lg border bg-emerald-50 p-4">
          <div className="text-xs text-emerald-700 uppercase tracking-wide">Paid income</div>
          <div className="text-2xl font-bold text-emerald-900" data-testid="sp-paid-income">
            €{(fin.paid_income || 0).toFixed(2)}
          </div>
          <div className="text-xs text-emerald-800 mt-1">{data.purchases_count_paid || 0} purchase(s)</div>
        </div>
        <div className="rounded-lg border bg-amber-50 p-4">
          <div className="text-xs text-amber-700 uppercase tracking-wide">Pending</div>
          <div className="text-2xl font-bold text-amber-900" data-testid="sp-pending-income">
            €{(fin.pending_income || 0).toFixed(2)}
          </div>
          <div className="text-xs text-amber-800 mt-1">{data.purchases_count_pending || 0} purchase(s)</div>
        </div>
        <div className="rounded-lg border bg-slate-50 p-4">
          <div className="text-xs text-slate-600 uppercase tracking-wide">Refunded</div>
          <div className="text-2xl font-bold text-slate-800" data-testid="sp-refunded-income">
            €{(fin.refunded_income || 0).toFixed(2)}
          </div>
          <div className="text-xs text-slate-600 mt-1">{data.purchases_count_refunded || 0} purchase(s)</div>
        </div>
        <div className="rounded-lg border bg-blue-50 p-4">
          <div className="text-xs text-blue-700 uppercase tracking-wide">Total purchases</div>
          <div className="text-2xl font-bold text-blue-900" data-testid="sp-total-count">
            {data.purchases_count_total || 0}
          </div>
        </div>
      </div>

      {/* Purchases table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Purchases</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Member #</TableHead>
                  <TableHead>Price row</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead>Purchased</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No purchases yet for this season pass.
                    </TableCell>
                  </TableRow>
                ) : purchases.map(p => (
                  <TableRow key={p.id} data-testid={`sp-purchase-row-${p.id}`}>
                    <TableCell className="font-medium">{p.user_name || '—'}</TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[220px] truncate" title={p.user_email}>
                      {p.user_email || '—'}
                    </TableCell>
                    <TableCell className="text-sm">{p.member_number || '—'}</TableCell>
                    <TableCell className="text-sm">{p.price_row_label || '—'}</TableCell>
                    <TableCell className="text-right font-medium">
                      €{(p.amount_paid || 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded ${statusColor(p.payment_status)}`}>
                        {p.payment_status}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {p.purchased_at ? new Date(p.purchased_at).toLocaleDateString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ── Main wrapper with navigation state ──
const EventFinancialManagement = () => {
  const [selectedEventId, setSelectedEventId] = useState(null);

  if (selectedEventId) {
    // Season pass rows use "season-pass:<id>" as their id — route to the
    // dedicated season-pass detail view; everything else uses the standard
    // event-detail view.
    if (typeof selectedEventId === "string" && selectedEventId.startsWith("season-pass:")) {
      const spId = selectedEventId.split(":", 2)[1];
      return <SeasonPassFinancialDetail seasonPassId={spId} onBack={() => setSelectedEventId(null)} />;
    }
    return <EventFinancialDetail eventId={selectedEventId} onBack={() => setSelectedEventId(null)} />;
  }
  return <EventFinancialOverview onSelectEvent={setSelectedEventId} />;
};

export default EventFinancialManagement;
